// @ts-nocheck
/**
 * sensorControlConfig.ts — defines the interactive controls shown in the
 * SensorControlPanel for each sensor component type.
 *
 * Used by:
 *  - SensorControlPanel.tsx  (renders the controls)
 *  - SimulatorCanvas.tsx     (decides whether to show the panel on click)
 */

export interface SliderControl {
  type: "slider";
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
  unit: string;
  defaultValue: number;
  /** Optional custom formatter — e.g. to show "24.0°C" instead of "24" */
  formatValue?: (v: number) => string;
}

export interface ButtonControl {
  type: "button";
  key: string;
  label: string;
}

export interface ToggleControl {
  type: "toggle";
  key: string;
  label: string;
  onLabel?: string;
  offLabel?: string;
  defaultValue: boolean;
}

export interface OptionControl {
  type: "options";
  key: string;
  label: string;
  options: { label: string; value: number | string; hint?: string }[];
  defaultValue: number | string;
}

export type SensorControl =
  | SliderControl
  | ButtonControl
  | ToggleControl
  | OptionControl;

export interface SensorControlDef {
  title: string;
  controls: SensorControl[];
  defaultValues: Record<string, number | boolean | string>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const oneDecimal = (v: number) => v.toFixed(1);
const twoDecimal = (v: number) => v.toFixed(2);

// ─── Sensor Control Definitions ──────────────────────────────────────────────

export const SENSOR_CONTROLS: Record<string, SensorControlDef> = {
  // ── MPU-6050 6-axis IMU ────────────────────────────────────────────────────
  mpu6050: {
    title: "MPU6050 Accelerometer + Gyroscope",
    controls: [
      // Acceleration
      {
        type: "slider",
        key: "accelX",
        label: "X",
        min: -2,
        max: 2,
        step: 0.01,
        unit: "g",
        defaultValue: 0,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "accelY",
        label: "Y",
        min: -2,
        max: 2,
        step: 0.01,
        unit: "g",
        defaultValue: 0,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "accelZ",
        label: "Z",
        min: -2,
        max: 2,
        step: 0.01,
        unit: "g",
        defaultValue: 1,
        formatValue: oneDecimal,
      },
      // Rotation (gyro)
      {
        type: "slider",
        key: "gyroX",
        label: "X",
        min: -250,
        max: 250,
        step: 1,
        unit: "°/sec",
        defaultValue: 0,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "gyroY",
        label: "Y",
        min: -250,
        max: 250,
        step: 1,
        unit: "°/sec",
        defaultValue: 0,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "gyroZ",
        label: "Z",
        min: -250,
        max: 250,
        step: 1,
        unit: "°/sec",
        defaultValue: 0,
        formatValue: oneDecimal,
      },
      // Temperature
      {
        type: "slider",
        key: "temp",
        label: "Temperature",
        min: -40,
        max: 85,
        step: 1,
        unit: "°C",
        defaultValue: 24,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: {
      accelX: 0,
      accelY: 0,
      accelZ: 1,
      gyroX: 0,
      gyroY: 0,
      gyroZ: 0,
      temp: 24,
    },
  },

  // ── DHT22 Temperature / Humidity ──────────────────────────────────────────
  dht22: {
    title: "DHT22 Temperature & Humidity",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: -40,
        max: 100,
        step: 0.5,
        unit: "°C",
        defaultValue: 25,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "humidity",
        label: "Humidity",
        min: 0,
        max: 100,
        step: 0.5,
        unit: "%",
        defaultValue: 50,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: { temperature: 25, humidity: 50 },
  },

  dht11: {
    title: "DHT11 Temperature & Humidity",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: 0,
        max: 50,
        step: 1,
        unit: "°C",
        defaultValue: 25,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "humidity",
        label: "Humidity",
        min: 20,
        max: 90,
        step: 1,
        unit: "%",
        defaultValue: 50,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: { temperature: 25, humidity: 50 },
  },

  // ── BMP280 Barometric Pressure + Temperature ───────────────────────────────
  bmp280: {
    title: "BMP280 Barometric Pressure Sensor",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: -40,
        max: 85,
        step: 1,
        unit: "°C",
        defaultValue: 24,
        formatValue: oneDecimal,
      },
      {
        type: "slider",
        key: "pressure",
        label: "Pressure",
        min: 300,
        max: 1100,
        step: 0.25,
        unit: "hPa",
        defaultValue: 1013.25,
        formatValue: twoDecimal,
      },
    ],
    defaultValues: { temperature: 24, pressure: 1013.25 },
  },

  // ── HC-SR04 Ultrasonic Distance ───────────────────────────────────────────
  "hc-sr04": {
    title: "Ultrasonic Distance Sensor",
    controls: [
      {
        type: "slider",
        key: "distance",
        label: "Distance",
        min: 2,
        max: 400,
        step: 1,
        unit: "cm",
        defaultValue: 10,
      },
    ],
    defaultValues: { distance: 10 },
  },

  // ── Photoresistor (LDR) ───────────────────────────────────────────────────
  "photoresistor-sensor": {
    title: "Photoresistor (LDR)",
    controls: [
      {
        type: "slider",
        key: "lux",
        label: "Illumination",
        min: 0,
        max: 1000,
        step: 1,
        unit: "lux",
        defaultValue: 500,
      },
    ],
    defaultValues: { lux: 500 },
  },

  // ── Photodiode ────────────────────────────────────────────────────────────
  photodiode: {
    title: "Photodiode",
    controls: [
      {
        type: "slider",
        key: "lux",
        label: "Illumination",
        min: 0,
        max: 1000,
        step: 1,
        unit: "lux",
        defaultValue: 500,
      },
    ],
    defaultValues: { lux: 500 },
  },

  // ── PIR Motion Sensor ─────────────────────────────────────────────────────
  "pir-motion-sensor": {
    title: "PIR Motion Sensor",
    controls: [{ type: "button", key: "trigger", label: "Simulate motion" }],
    defaultValues: {},
  },

  // ── NTC Temperature Sensor ────────────────────────────────────────────────
  "ntc-temperature-sensor": {
    title: "NTC Temperature Sensor",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: -40,
        max: 125,
        step: 1,
        unit: "°C",
        defaultValue: 25,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: { temperature: 25 },
  },

  lm35dz: {
    title: "LM35DZ Temperature Sensor",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: 0,
        max: 100,
        step: 1,
        unit: "°C",
        defaultValue: 25,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: { temperature: 25 },
  },

  thermistor: {
    title: "Thermistor",
    controls: [
      {
        type: "slider",
        key: "temperature",
        label: "Temperature",
        min: -40,
        max: 125,
        step: 1,
        unit: "°C",
        defaultValue: 25,
        formatValue: oneDecimal,
      },
    ],
    defaultValues: { temperature: 25 },
  },

  "water-level-sensor": {
    title: "Water Level Sensor",
    controls: [
      {
        type: "slider",
        key: "level",
        label: "Water Level",
        min: 0,
        max: 100,
        step: 1,
        unit: "%",
        defaultValue: 0,
      },
    ],
    defaultValues: { level: 0 },
  },

  "rc522-rfid": {
    title: "RC522 RFID Module",
    controls: [
      {
        type: "toggle",
        key: "cardPresent",
        label: "Card in field",
        onLabel: "Tapped",
        offLabel: "Removed",
        defaultValue: false,
      },
      {
        type: "options",
        key: "uid",
        label: "Card UID",
        options: [
          { label: "Blue keyfob", value: "DE AD BE EF", hint: "DE AD BE EF" },
          { label: "White card", value: "A1 B2 C3 D4", hint: "A1 B2 C3 D4" },
          { label: "Access tag", value: "12 34 56 78", hint: "12 34 56 78" },
          { label: "Unknown card", value: "FF FF FF FF", hint: "FF FF FF FF" },
        ],
        defaultValue: "DE AD BE EF",
      },
    ],
    defaultValues: { cardPresent: false, uid: "DE AD BE EF" },
  },

  // ── LCD1602 I2C Adapter (PCF8574) ─────────────────────────────────────────
  "lcd1602-i2c": {
    title: "LCD1602 I2C Adapter",
    controls: [
      {
        type: "slider",
        key: "contrast",
        label: "Contrast trimpot",
        min: 0,
        max: 100,
        step: 1,
        unit: "%",
        defaultValue: 50,
      },
    ],
    defaultValues: { contrast: 50 },
  },

  // ── Gas Sensor (MQ-series) ────────────────────────────────────────────────
  "gas-sensor": {
    title: "Gas Sensor (MQ-series)",
    controls: [
      {
        type: "slider",
        key: "gasLevel",
        label: "Gas Level",
        min: 0,
        max: 1023,
        step: 1,
        unit: "",
        defaultValue: 100,
      },
    ],
    defaultValues: { gasLevel: 100 },
  },

  // ── Flame Sensor ──────────────────────────────────────────────────────────
  "flame-sensor": {
    title: "Flame Sensor",
    controls: [
      {
        type: "slider",
        key: "intensity",
        label: "Flame Intensity",
        min: 0,
        max: 1023,
        step: 1,
        unit: "",
        defaultValue: 0,
      },
    ],
    defaultValues: { intensity: 0 },
  },

  // ── Big Sound Sensor (FC-04) ──────────────────────────────────────────────
  "big-sound-sensor": {
    title: "Sound Sensor",
    controls: [
      {
        type: "slider",
        key: "soundLevel",
        label: "Sound Level",
        min: 0,
        max: 1023,
        step: 1,
        unit: "",
        defaultValue: 512,
      },
    ],
    defaultValues: { soundLevel: 512 },
  },

  // ── Small Sound Sensor (KY-038) ───────────────────────────────────────────
  "small-sound-sensor": {
    title: "Sound Sensor (KY-038)",
    controls: [
      {
        type: "slider",
        key: "soundLevel",
        label: "Sound Level",
        min: 0,
        max: 1023,
        step: 1,
        unit: "",
        defaultValue: 512,
      },
    ],
    defaultValues: { soundLevel: 512 },
  },

  "microphone-module": {
    title: "Microphone Module",
    controls: [
      {
        type: "slider",
        key: "soundLevel",
        label: "Sound Level",
        min: 0,
        max: 1023,
        step: 1,
        unit: "",
        defaultValue: 512,
      },
    ],
    defaultValues: { soundLevel: 512 },
  },

  "obstacle-avoidance-module": {
    title: "Obstacle Avoidance Module",
    controls: [
      {
        type: "slider",
        key: "distance",
        label: "Obstacle Distance",
        min: 2,
        max: 80,
        step: 1,
        unit: "cm",
        defaultValue: 100,
      },
    ],
    defaultValues: { distance: 100 },
  },

  // ── Tilt Switch ───────────────────────────────────────────────────────────
  "tilt-switch": {
    title: "Tilt Switch",
    controls: [{ type: "button", key: "toggle", label: "Toggle tilt" }],
    defaultValues: {},
  },

  "vibration-switch": {
    title: "HDX Vibration Switch",
    controls: [
      { type: "button", key: "pulse", label: "Pulse vibration" },
      { type: "button", key: "toggle", label: "Toggle output" },
    ],
    defaultValues: {},
  },

  // ── Analog Joystick ───────────────────────────────────────────────────────
  "analog-joystick": {
    title: "Analog Joystick",
    controls: [
      {
        type: "slider",
        key: "xAxis",
        label: "X Axis",
        min: -512,
        max: 512,
        step: 1,
        unit: "",
        defaultValue: 0,
      },
      {
        type: "slider",
        key: "yAxis",
        label: "Y Axis",
        min: -512,
        max: 512,
        step: 1,
        unit: "",
        defaultValue: 0,
      },
    ],
    defaultValues: { xAxis: 0, yAxis: 0 },
  },
};
